// src/config/offerConfig.js
/**
 * Unified Offer System Configuration
 * Centralizes all offer-related styling, constants, and behaviors
 * Uses standard PWA colors from tailwind.config.js (farmgreen, farmgold)
 */

export const OFFER_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  COMPLETED: "completed",
  DECLINED: "declined",
  CANCELLED: "cancelled",
};

// Simple status styling using PWA colors - only where color adds meaning
export const OFFER_STATUS_STYLE = {
  [OFFER_STATUS.PENDING]: {
    bg: "bg-farmgold-50 dark:bg-farmgold-950/10",
    border: "border-farmgold-200 dark:border-farmgold-900/20",
    text: "text-farmgold-700 dark:text-farmgold-300",
    badge: "bg-farmgold-100 dark:bg-farmgold-900/20 text-farmgold-700 dark:text-farmgold-400",
  },
  [OFFER_STATUS.ACCEPTED]: {
    bg: "bg-farmgreen-50 dark:bg-farmgreen-950/10",
    border: "border-farmgreen-200 dark:border-farmgreen-900/20",
    text: "text-farmgreen-700 dark:text-farmgreen-300",
    badge: "bg-farmgreen-100 dark:bg-farmgreen-900/20 text-farmgreen-700 dark:text-farmgreen-400",
  },
  [OFFER_STATUS.COMPLETED]: {
    bg: "bg-farmgreen-50 dark:bg-farmgreen-950/10",
    border: "border-farmgreen-200 dark:border-farmgreen-900/20",
    text: "text-farmgreen-700 dark:text-farmgreen-300",
    badge: "bg-farmgreen-100 dark:bg-farmgreen-900/20 text-farmgreen-700 dark:text-farmgreen-400",
  },
  [OFFER_STATUS.DECLINED]: {
    bg: "bg-red-50 dark:bg-red-950/10",
    border: "border-red-200 dark:border-red-900/20",
    text: "text-red-700 dark:text-red-300",
    badge: "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400",
  },
  [OFFER_STATUS.CANCELLED]: {
    bg: "bg-gray-50 dark:bg-gray-950/10",
    border: "border-gray-200 dark:border-gray-900/20",
    text: "text-gray-700 dark:text-gray-300",
    badge: "bg-gray-100 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400",
  },
};

// Button styling - only on primary action buttons
export const OFFER_BUTTONS = {
  accept: "bg-farmgreen-600 hover:bg-farmgreen-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95",
  decline: "bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95",
  cancel: "border-2 border-border text-foreground hover:bg-muted transition-all disabled:opacity-50 disabled:cursor-not-allowed",
  primary: "bg-farmgreen-600 hover:bg-farmgreen-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95",
  secondary: "border-2 border-border text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-95",
};

// Modal styling - theme aware, no heavy shadows
export const OFFER_MODAL = {
  backdrop: "fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 px-4",
  container: "bg-card dark:bg-card rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 sm:p-8",
  header: "text-2xl font-bold text-foreground",
  label: "text-sm font-medium text-muted-foreground",
  input: "w-full px-4 py-3 border-b-2 border-border bg-transparent text-lg font-medium text-foreground placeholder-muted-foreground focus:outline-none focus:border-farmgreen-600 focus:border-focus-ring transition",
  button: "w-full mt-6 px-6 py-4 font-semibold text-base rounded-lg transition-all flex items-center justify-center gap-2",
};

// In-chat offer display styling - light, no card, no shadow
export const OFFER_IN_CHAT = {
  container: "bg-card dark:bg-card border-t border-border px-3 py-2.5",
  header: "font-semibold text-foreground flex items-center gap-2",
  amount: "text-farmgreen-600 font-bold text-xl dark:text-farmgreen-400",
  status: "text-xs text-muted-foreground mt-1",
  actionContainer: "flex gap-2 mt-3 flex-wrap",
};

// Validation
export const OFFER_RULES = {
  minAmount: 0,
  maxMessageLength: 500,
  requiresChat: true,
  requiresProduct: true,
  requiresSeller: true,
  requiresAuth: true,
};

// Status transitions
export const OFFER_TRANSITIONS = {
  [OFFER_STATUS.PENDING]: [OFFER_STATUS.ACCEPTED, OFFER_STATUS.DECLINED, OFFER_STATUS.CANCELLED],
  [OFFER_STATUS.ACCEPTED]: [OFFER_STATUS.CANCELLED],
  [OFFER_STATUS.DECLINED]: [],
  [OFFER_STATUS.CANCELLED]: [],
};

// Notification messages - no quotes around product titles
export const OFFER_NOTIFICATIONS = {
  sendSuccess: (amount) => `Offer of ${amount} RWF sent successfully`,
  acceptSuccess: (amount) => `Offer of ${amount} RWF accepted`,
  declineSuccess: (amount) => `Offer of ${amount} RWF declined`,
  buyerNotifyAccept: (productTitle, amount) =>
    `Your offer of ${amount} RWF${productTitle ? ` for ${productTitle}` : ""} was accepted`,
  sellerNotifyReceive: (buyerName, amount, productTitle) =>
    `${buyerName} made an offer of ${amount} RWF${productTitle ? ` for ${productTitle}` : ""}`,
};

export default {
  STATUS: OFFER_STATUS,
  STATUS_STYLE: OFFER_STATUS_STYLE,
  BUTTONS: OFFER_BUTTONS,
  MODAL: OFFER_MODAL,
  IN_CHAT: OFFER_IN_CHAT,
  RULES: OFFER_RULES,
  TRANSITIONS: OFFER_TRANSITIONS,
  NOTIFICATIONS: OFFER_NOTIFICATIONS,
};
